/**
 * Platform detection utilities for feature gating.
 */

/** Returns true if the device is iOS (iPhone/iPad/iPod) */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Returns true if the app is running as a PWA (standalone / fullscreen) */
export function isPWA(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (window.navigator as any).standalone === true;
}

/** Returns true if running as an iOS PWA (Add to Home Screen) */
export function isIOSPwa(): boolean {
  return isIOS() && isPWA();
}

/** Returns true if running in a regular browser on iOS (not native, not PWA) */
export function isIOSBrowser(): boolean {
  return isIOS() && !isPWA();
}

/**
 * Features that do NOT work on iOS PWA and should be disabled/grayed out.
 * These features rely on native OS capabilities (FLAG_SECURE, biometric keychain, Tor, etc.)
 * that iOS WebKit does not expose.
 */
export const IOS_PWA_UNSUPPORTED_FEATURES = {
  /** FLAG_SECURE / hardware screenshot blocking — not available on WebKit */
  screenshotProtection: true,
  /** Native biometric keychain access — WebAuthn only, no Keychain */
  nativeBiometric: true,
  /** Tor integration — not possible in WebKit sandbox */
  torSupport: true,
} as const;

/**
 * Returns true if the given feature should be force-disabled on the current platform.
 */
export function isFeatureUnsupported(feature: keyof typeof IOS_PWA_UNSUPPORTED_FEATURES): boolean {
  if (!isIOS()) return false;
  return IOS_PWA_UNSUPPORTED_FEATURES[feature] ?? false;
}
